import { _ as _object_spread } from "@swc/helpers/_/_object_spread";
import { _ as _object_spread_props } from "@swc/helpers/_/_object_spread_props";
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { DndContext, closestCenter, MouseSensor, TouchSensor, useSensor, useSensors, DragOverlay } from '@dnd-kit/core';
import { SortableContext } from '@dnd-kit/sortable';
import { fixedSpacingValue, spacingValue } from '../../foundations/spacing';
import { zIndexScale } from '../../foundations/zIndex.constants';
import { DragHandle } from '../../icons/essentials/DragHandle';
import { useDevice } from '../../providers/Device';
import { getTestIds } from '../../utils/TestIds';
import { useBiAction } from '../BiTracking';
import { useBottomSheetContainer } from '../BottomSheet/BottomSheetContainerContext';
import { DraggableComponent } from './DraggableComponent';
import styles from './DraggableGrid.module.scss';
import { dropIndicatorFor, sortingStrategyFor, readInheritedItemStyle, reorderItems, resolveDragModifiers, toOverlayStyle } from './DraggableGrid.utils';
export const DraggableGrid = (param)=>{
    let { items, itemComponent: ItemComponent, onReorder, dragMode, gap, gapMobile, className, itemClassName, itemStyle, testId, itemHandleVisibility = 'hover', itemHandleSize = 'normal', itemMotion, itemDropIndicator = 'none', itemDraggedAppearance = 'none', itemDragActivation: itemDragActivationProp = 'handle', itemHandlePosition = 'outside', layout, onDragStateChange, bi, noBi: _noBi } = param;
    const { isMobileDevice } = useDevice();
    const { container: overlayContainer } = useBottomSheetContainer();
    const itemDragActivation = isMobileDevice ? 'full' : itemDragActivationProp;
    const overlayHandle = /*#__PURE__*/ _jsx("div", {
        className: "".concat(styles.dragHandle, " ").concat(styles.handleAlways, " ").concat(itemHandleSize === 'small' ? styles.handleSmall : ''),
        children: /*#__PURE__*/ _jsx(DragHandle, {
            size: itemHandleSize === 'small' ? 14 : 20
        })
    });
    const touchAction = itemDragActivation === 'full' ? isMobileDevice ? 'pan-y' : 'none' : undefined;
    const Item = ItemComponent;
    const testIds = useMemo(()=>getTestIds(testId), [
        testId
    ]);
    const logBiAction = useBiAction();
    const [activeId, setActiveId] = useState(null);
    const [overId, setOverId] = useState(null);
    const gridRef = useRef(null);
    const [minItemHeight, setMinItemHeight] = useState(null);
    const [inheritedItemStyle, setInheritedItemStyle] = useState(null);
    const mouseSensor = useSensor(MouseSensor, {
        activationConstraint: {
            distance: 5
        }
    });
    const touchSensor = useSensor(TouchSensor, {
        activationConstraint: {
            delay: 200,
            tolerance: 5
        }
    });
    const sensors = useSensors(mouseSensor, touchSensor);
    const dragModifiers = resolveDragModifiers(layout);
    const resolvedGap = gapMobile != null ? fixedSpacingValue(isMobileDevice ? gapMobile : gap) : spacingValue(gap);
    const gridStyle = useMemo(()=>{
        const template = 'template' in layout ? layout.template : undefined;
        const columns = 'columns' in layout ? layout.columns : undefined;
        const rows = 'rows' in layout ? layout.rows : undefined;
        const autoColumns = rows ? Math.ceil(items.length / rows) : undefined;
        return {
            display: 'grid',
            gridTemplateColumns: template !== null && template !== void 0 ? template : columns ? "repeat(".concat(columns, ", minmax(0, 1fr))") : autoColumns ? "repeat(".concat(autoColumns, ", minmax(0, 1fr))") : undefined,
            gridTemplateRows: rows ? "repeat(".concat(rows, ", 1fr)") : undefined,
            gridAutoFlow: rows ? 'column' : undefined,
            gap: resolvedGap
        };
    }, [
        layout,
        resolvedGap,
        items.length
    ]);
    const handleDragStart = (event)=>{
        setActiveId(String(event.active.id));
        if (dragMode === 'withGhost' && gridRef.current) setInheritedItemStyle(readInheritedItemStyle(gridRef.current));
        onDragStateChange === null || onDragStateChange === void 0 ? void 0 : onDragStateChange(true);
    };
    const handleDragOver = (event)=>setOverId(event.over ? String(event.over.id) : null);
    const handleDragEnd = (event)=>{
        setActiveId(null);
        setOverId(null);
        onDragStateChange === null || onDragStateChange === void 0 ? void 0 : onDragStateChange(false);
        const { active, over } = event;
        if (over && active.id !== over.id) {
            onReorder(reorderItems(items, String(active.id), String(over.id)));
            if (bi === null || bi === void 0 ? void 0 : bi.name) {
                logBiAction({
                    entityType: 'draggable_grid',
                    entityName: bi.name,
                    actionType: 'reorder',
                    data: _object_spread_props(_object_spread({}, bi.data), {
                        testId: testId !== null && testId !== void 0 ? testId : '',
                        fromId: String(active.id),
                        toId: String(over.id)
                    })
                });
            }
        }
    };
    const handleDragCancel = ()=>{
        setActiveId(null);
        setOverId(null);
        onDragStateChange === null || onDragStateChange === void 0 ? void 0 : onDragStateChange(false);
    };
    useEffect(()=>{
        if (!activeId || !gridRef.current) {
            setMinItemHeight(null);
            return;
        }
        let innerFrameId;
        const outerFrameId = requestAnimationFrame(()=>{
            innerFrameId = requestAnimationFrame(()=>{
                var _ref;
                if (!gridRef.current) return;
                const sortableItems = Array.from(gridRef.current.children);
                const activeIndex = items.findIndex((item)=>item.id === activeId);
                const activeEl = activeIndex >= 0 ? sortableItems[activeIndex] : null;
                const height = (_ref = activeEl === null || activeEl === void 0 ? void 0 : activeEl.offsetHeight) !== null && _ref !== void 0 ? _ref : 0;
                if (height > 0) setMinItemHeight(height);
            });
        });
        return ()=>{
            cancelAnimationFrame(outerFrameId);
            cancelAnimationFrame(innerFrameId);
        };
    }, [
        activeId,
        items
    ]);
    const isDragActive = activeId !== null;
    const activeItem = isDragActive ? items.find((item)=>item.id === activeId) : undefined;
    const gridClassName = [
        styles.grid,
        className
    ].filter(Boolean).join(' ');
    const handlePointerDown = ()=>{
        document.body.style.userSelect = 'none';
    };
    const handlePointerUp = ()=>{
        document.body.style.userSelect = '';
    };
    return /*#__PURE__*/ _jsxs(DndContext, {
        sensors: sensors,
        modifiers: dragModifiers,
        collisionDetection: closestCenter,
        onDragStart: handleDragStart,
        onDragOver: handleDragOver,
        onDragEnd: handleDragEnd,
        onDragCancel: handleDragCancel,
        children: [
            /*#__PURE__*/ _jsx(SortableContext, {
                items: items,
                strategy: sortingStrategyFor(itemMotion),
                children: /*#__PURE__*/ _jsx("div", {
                    ref: gridRef,
                    className: gridClassName,
                    style: gridStyle,
                    "data-testid": testIds.grid(),
                    onPointerDown: handlePointerDown,
                    onPointerUp: handlePointerUp,
                    onPointerCancel: handlePointerUp,
                    children: items.map((item)=>/*#__PURE__*/ _jsx(DraggableComponent, {
                            id: item.id,
                            testIds: testIds,
                            dragMode: dragMode,
                            itemHandleVisibility: itemHandleVisibility,
                            itemHandleSize: itemHandleSize,
                            itemDragActivation: itemDragActivation,
                            itemHandlePosition: itemHandlePosition,
                            touchAction: touchAction,
                            minHeight: item.id === activeId ? minItemHeight !== null && minItemHeight !== void 0 ? minItemHeight : undefined : undefined,
                            className: typeof itemClassName === 'function' ? itemClassName(item) : itemClassName,
                            itemStyle: itemStyle === null || itemStyle === void 0 ? void 0 : itemStyle(item),
                            itemDropIndicator: itemDropIndicator,
                            itemDraggedAppearance: itemDraggedAppearance,
                            dropIndicator: dropIndicatorFor({
                                items,
                                activeId,
                                overId,
                                itemId: item.id
                            }),
                            children: (param)=>{
                                let { dragHandle } = param;
                                const handleProps = itemHandlePosition === 'inside' ? {
                                    dragHandle
                                } : {};
                                return /*#__PURE__*/ _jsx(Item, _object_spread(_object_spread_props(_object_spread({}, item), {
                                    isDragActive: isDragActive,
                                    isItemDragging: item.id === activeId
                                }), handleProps));
                            }
                        }, item.id))
                })
            }),
            dragMode === 'withGhost' && /*#__PURE__*/ createPortal(/*#__PURE__*/ _jsx(DragOverlay, {
                zIndex: zIndexScale.dragOverlay,
                children: activeItem ? /*#__PURE__*/ _jsxs("div", {
                    className: styles.dragOverlay,
                    style: inheritedItemStyle ? toOverlayStyle(inheritedItemStyle) : undefined,
                    "data-testid": testIds.dragOverlay(),
                    children: [
                        itemHandlePosition === 'outside' && overlayHandle,
                        /*#__PURE__*/ _jsx("div", {
                            className: styles.itemContent,
                            children: itemHandlePosition === 'inside' ? /*#__PURE__*/ _jsx(Item, _object_spread_props(_object_spread({}, activeItem), {
                                isDragActive: true,
                                isDragOverlay: true,
                                dragHandle: overlayHandle
                            })) : /*#__PURE__*/ _jsx(Item, _object_spread_props(_object_spread({}, activeItem), {
                                isDragActive: true,
                                isDragOverlay: true
                            }))
                        })
                    ]
                }) : null
            }), overlayContainer !== null && overlayContainer !== void 0 ? overlayContainer : document.body)
        ]
    });
};
